import { useCallback, useState, useEffect } from 'react';
import { CellValueChangedEvent } from 'ag-grid-community';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { AgileTableRow, ZoneCapacity } from '@components/agile/types/AgileTableRow';
import useGetAsinMetadataResults from 'src/store/hooks/useGetAsinMetadataResult';

interface UseAgileManagementEventHandlersProps {
  rowData: AgileTableRow[];
  setRowData: React.Dispatch<React.SetStateAction<AgileTableRow[]>>;
  zoneCapacities: ZoneCapacity[];
  macroplanogramList: AgileMacroplanogram[] | null | undefined;
  setPendingAsins: React.Dispatch<React.SetStateAction<string[]>>;
  productGroup?: string;
  store?: string;
  zoneMapConfigId?: string;
}

export const useAgileManagementEventHandlers = ({
  setRowData,
  zoneCapacities,
  macroplanogramList,
  setPendingAsins,
  productGroup = 'produce',
  store,
  zoneMapConfigId,
}: UseAgileManagementEventHandlersProps) => {
  // State to track when we need to fetch metadata for subcategory changes
  const [pendingSubcategoryUpdate, setPendingSubcategoryUpdate] = useState<{
    asin: string;
    zoneName: string;
    subcategoryId: string;
  } | null>(null);

  // Fetch metadata when we have a pending subcategory update
  const { asinMetadataList } = useGetAsinMetadataResults({
    asinList: pendingSubcategoryUpdate ? [pendingSubcategoryUpdate.asin] : [],
    storeId: store || '',
    department: productGroup,
    zoneName: pendingSubcategoryUpdate?.zoneName,
    zoneMapConfigId,
  });

  // Update row data when metadata is fetched
  useEffect(() => {
    if (pendingSubcategoryUpdate && asinMetadataList && asinMetadataList.length > 0) {
      const metadata = asinMetadataList[0];

      setRowData((prevData) => {
        return prevData.map((row) => {
          if (row.asin === pendingSubcategoryUpdate.asin && row.zoneName === pendingSubcategoryUpdate.zoneName) {
            const newCapacity = metadata.capacityUnitPerFacing?.capacity || row.fohCapacity.capacity;
            return {
              ...row,
              subcategoryId: pendingSubcategoryUpdate.subcategoryId,
              fohCapacity: {
                capacity: newCapacity,
                jumbleFactor: metadata.capacityUnitPerFacing?.jumbleFactor || row.fohCapacity.jumbleFactor,
                errorMsg: metadata.capacityUnitPerFacing?.errorMsg || row.fohCapacity.errorMsg,
              },
              remainingCapacity: newCapacity ? newCapacity * row.remainingFacings : row.remainingCapacity,
            };
          }
          return row;
        });
      });

      // Clear the pending update
      setPendingSubcategoryUpdate(null);
    }
  }, [asinMetadataList, pendingSubcategoryUpdate, setRowData]);
  const handleZoneChange = useCallback(
    (asin: string, oldZone: string, newZone: string, rowId?: string) => {
      setRowData((prevData) => {
        // Find the row being moved using rowId if available, otherwise fall back to asin+zone
        const movingRowIndex = rowId
          ? prevData.findIndex((row) => row.rowId === rowId)
          : prevData.findIndex((row) => {
              if (row.asin !== asin) return false;
              // Handle "Unassigned" zone matching
              if (oldZone === 'Unassigned') {
                return !row.zoneName || row.zoneName === 'Unassigned' || row.zoneName === '';
              }
              return row.zoneName === oldZone;
            });
        if (movingRowIndex === -1) return prevData;

        const movingRow = prevData[movingRowIndex];
        const zoneCapacity = zoneCapacities.find((zc) => zc.zoneName.toLowerCase() === newZone.toLowerCase());
        const macroplanogram = macroplanogramList?.find((mp) => mp.macroplanogramName === newZone);

        // Check if there's already an existing row for this ASIN in the target zone
        const existingRowIndex = prevData.findIndex(
          (row) => row.asin === asin && row.zoneName === newZone && !('isNewAsin' in row && row.isNewAsin), // Only merge with existing ASINs, not other new ASINs
        );

        let updatedData;

        if (existingRowIndex !== -1 && 'isNewAsin' in movingRow && movingRow.isNewAsin) {
          // Merge: Add the new ASIN's facings to the existing row
          updatedData = prevData
            .map((row, index) => {
              if (index === existingRowIndex) {
                return {
                  ...row,
                  remainingFacings: row.remainingFacings + movingRow.remainingFacings,
                  difference: row.remainingFacings + movingRow.remainingFacings - row.availableFacings,
                  remainingCapacity:
                    (row.fohCapacity.capacity || 0) * (row.remainingFacings + movingRow.remainingFacings),
                };
              }
              return row;
            })
            .filter((_, index) => index !== movingRowIndex); // Remove the moving row

          // Remove from pendingAsins since it's now merged with existing
          setPendingAsins((prev) => prev.filter((pendingAsin) => pendingAsin !== asin));
        } else {
          // Normal zone change: update the row's zone info
          updatedData = prevData.map((row) => {
            if (row.asin === asin && row.zoneName === oldZone) {
              return {
                ...row,
                zoneName: newZone,
                zoneFacings: zoneCapacity?.totalFacings || 0,
                macroplanogramId: macroplanogram?.entryId || '',
                subcategoryId: '', // Reset subcategory when zone changes
              };
            }
            return row;
          });

          // Remove from pendingAsins if it's a new ASIN being assigned to a zone
          if ('isNewAsin' in movingRow && movingRow.isNewAsin) {
            setPendingAsins((prev) => prev.filter((pendingAsin) => pendingAsin !== asin));
          }
        }

        // Recalculate remaining zone facings for all zones
        const zoneUsedFacings = updatedData.reduce(
          (acc, item) => {
            const zoneName = item.zoneName;
            if (zoneName && zoneName !== 'Unassigned') {
              return { ...acc, [zoneName]: (acc[zoneName] || 0) + item.remainingFacings };
            }
            return acc;
          },
          {} as Record<string, number>,
        );

        return updatedData.map((item) => ({
          ...item,
          remainingZoneFacings:
            item.zoneFacings -
            (item.zoneName && item.zoneName !== 'Unassigned' ? zoneUsedFacings[item.zoneName] || 0 : 0),
        }));
      });
    },
    [zoneCapacities, macroplanogramList, setPendingAsins, setRowData],
  );

  const handleDeleteRow = useCallback(
    (asin: string, zoneName: string) => {
      // Remove from pendingAsins if it's a new ASIN
      setPendingAsins((prev) => prev.filter((pendingAsin) => pendingAsin !== asin));

      setRowData((prevData) => {
        // Check if this is a new ASIN
        const rowToDelete = prevData.find((row) => row.asin === asin && row.zoneName === zoneName);

        if (rowToDelete && 'isNewAsin' in rowToDelete && rowToDelete.isNewAsin) {
          // For new ASINs, completely remove from the list
          const filteredData = prevData.filter((row) => !(row.asin === asin && row.zoneName === zoneName));

          // Recalculate remaining zone facings after removal
          const zoneUsedFacings = filteredData.reduce(
            (acc, item) => {
              const zoneNameKey = item.zoneName || 'Unassigned';
              return { ...acc, [zoneNameKey]: (acc[zoneNameKey] || 0) + item.remainingFacings };
            },
            {} as Record<string, number>,
          );

          return filteredData.map((item) => ({
            ...item,
            remainingZoneFacings: item.zoneFacings - (zoneUsedFacings[item.zoneName || 'Unassigned'] || 0),
          }));
        } else {
          // For existing ASINs, set facings to 0
          const updatedData = prevData.map((row) => {
            if (row.asin === asin && row.zoneName === zoneName) {
              return {
                ...row,
                remainingFacings: 0,
                difference: -row.availableFacings,
                remainingCapacity: 0,
              };
            }
            return row;
          });

          // Recalculate remaining zone facings after setting to 0
          const zoneUsedFacings = updatedData.reduce(
            (acc, item) => {
              const zoneNameKey = item.zoneName;
              if (zoneNameKey) {
                return { ...acc, [zoneNameKey]: (acc[zoneNameKey] || 0) + item.remainingFacings };
              }
              return acc;
            },
            {} as Record<string, number>,
          );

          return updatedData.map((item) => ({
            ...item,
            remainingZoneFacings: item.zoneFacings - (item.zoneName ? zoneUsedFacings[item.zoneName] || 0 : 0),
          }));
        }
      });
    },
    [setPendingAsins, setRowData],
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      if (event.colDef.field === 'remainingFacings') {
        const newValue = parseInt(event.newValue, 10);

        // Update the rowData state directly
        setRowData((prevData) => {
          const updatedData = prevData.map((row) => {
            // Use rowId for accurate row identification
            if (row.rowId === event.data.rowId) {
              const difference = newValue - row.availableFacings;

              let newCapacity: number;
              const extendedRow = row as AgileTableRow & { originalFohCapacity?: number; isNewAsin?: boolean };

              // Check if this is a new ASIN (availableFacings = 0)
              if (row.availableFacings === 0 || extendedRow.isNewAsin) {
                // For new ASINs: store original per-unit capacity on first edit
                const originalPerUnitCapacity = extendedRow.originalFohCapacity ?? row.fohCapacity.capacity;
                newCapacity = Math.round((originalPerUnitCapacity || 0) * newValue);
              } else {
                // For existing ASINs: calculate per-unit from original data
                const originalCapacity = extendedRow.originalFohCapacity ?? row.fohCapacity.capacity;
                const fohCapacityPerUnit = (originalCapacity || 0) / row.availableFacings;
                newCapacity = Math.round(fohCapacityPerUnit * newValue);
              }

              return {
                ...row,
                remainingFacings: newValue,
                difference,
                remainingCapacity: newCapacity,
                fohCapacity: {
                  ...row.fohCapacity,
                  capacity: newCapacity,
                },
                originalFohCapacity: extendedRow.originalFohCapacity ?? row.fohCapacity.capacity,
              } as AgileTableRow & { originalFohCapacity: number };
            }
            return row;
          });

          // Recalculate remaining zone facings for all zones
          const zoneUsedFacings = updatedData.reduce(
            (acc, item) => {
              const zoneName = item.zoneName;
              if (zoneName) {
                return { ...acc, [zoneName]: (acc[zoneName] || 0) + item.remainingFacings };
              }
              return acc;
            },
            {} as Record<string, number>,
          );

          // Update remaining zone facings for all items
          return updatedData.map((item) => ({
            ...item,
            remainingZoneFacings: item.zoneFacings - (item.zoneName ? zoneUsedFacings[item.zoneName] || 0 : 0),
          }));
        });
      }
    },
    [setRowData],
  );

  const handleSubcategoryChange = useCallback((asin: string, zoneName: string, newSubcategoryId: string) => {
    // Set up the pending update to trigger metadata fetch
    setPendingSubcategoryUpdate({
      asin,
      zoneName,
      subcategoryId: newSubcategoryId,
    });
  }, []);

  return {
    handleZoneChange,
    handleDeleteRow,
    onCellValueChanged,
    handleSubcategoryChange,
  };
};
